import React from 'react';
import { Player, GameState } from '../../../domain/entities';
import { soundService } from '../../../services/SoundService';

interface PlayerActionsPanelProps {
    player: Player;
    isStarter: boolean;
    canTalk: boolean;
    cooldown: GameState['managerTalkCooldownEnd'] | { season: number; week: number; } | undefined;
    weeklyTalkLimitReached?: boolean;
    onTalk: () => void;
    onAssign: () => void;
    onRemove: () => void;
    onRenew: () => void;
    onSell: () => void;
    onUnlist: () => void;
    onRelease: () => void;
    onCompare?: () => void;
    onMakeOffer?: () => void;
    isPlayerTeamPlayer?: boolean;
    currentSeason?: number;
    currentWeek?: number;
    weeksPerSeason?: number;
}

const isYouthPlayerLocked = (player: Player, currentSeason?: number, currentWeek?: number, weeksPerSeason?: number): boolean => {
    if (!player.isPromotedYouth || !player.promotionDate || !currentSeason || !currentWeek || !weeksPerSeason) {
        return false;
    }

    const monthsInWeeks = Math.ceil((6 / 12) * weeksPerSeason);
    const weeksElapsed = (currentSeason - player.promotionDate.season) * weeksPerSeason + (currentWeek - player.promotionDate.week);

    return weeksElapsed < monthsInWeeks;
};

const getYouthLockMessage = (player: Player, currentSeason?: number, currentWeek?: number, weeksPerSeason?: number): string => {
    if (!isYouthPlayerLocked(player, currentSeason, currentWeek, weeksPerSeason)) {
        return '';
    }

    if (!player.promotionDate || !currentSeason || !currentWeek || !weeksPerSeason) {
        return '';
    }

    const monthsInWeeks = Math.ceil((6 / 12) * weeksPerSeason);
    const weeksElapsed = (currentSeason - player.promotionDate.season) * weeksPerSeason + (currentWeek - player.promotionDate.week);
    const weeksRemaining = monthsInWeeks - weeksElapsed;

    return `Youth player lock - ${weeksRemaining} weeks remaining`;
};

export const PlayerActionsPanel: React.FC<PlayerActionsPanelProps> = ({
    player, isStarter, canTalk, cooldown, weeklyTalkLimitReached, onTalk, onAssign, onRemove, onRenew, onSell, onUnlist, onRelease, onCompare, onMakeOffer, isPlayerTeamPlayer = true, currentSeason, currentWeek, weeksPerSeason
}) => {
    const sellButtonLocked = isYouthPlayerLocked(player, currentSeason, currentWeek, weeksPerSeason);
    const lockMessage = getYouthLockMessage(player, currentSeason, currentWeek, weeksPerSeason);

    // Determine the talk button tooltip message
    let talkButtonTitle = 'Initiate a conversation with this player';
    if (!canTalk) {
        if (weeklyTalkLimitReached) {
            talkButtonTitle = "You can't talk to any more players until next week";
        } else if (cooldown) {
            talkButtonTitle = `You can't talk to this player again until week ${Math.floor(cooldown.week)} (Season ${cooldown.season})`;
        }
    }

    return (
        <div className="panel">
            <div className="panel-header">Actions</div>
            <div className="panel-content player-actions">
                {isPlayerTeamPlayer && (
                    <>
                        <button
                            className="button"
                            onClick={onTalk}
                            title={talkButtonTitle}
                        >
                            Talk to Player {player.wantsToTalk && '💬'}
                        </button>
                        {player.injury?.type !== 'Severe' && (
                            isStarter ? (
                                <>
                                    <button className="button" onClick={onAssign}>Change Position</button>
                                    <button className="button secondary" onClick={onRemove}>Remove from XI</button>
                                </>
                            ) : (
                                <button className="button" onClick={onAssign}>Assign to XI</button>
                            )
                        )}
                        {player.contract.yearsRemaining <= 1 && (
                            <button className="button" onClick={onRenew}>Renew Contract</button>
                        )}
                        <button className="button" onClick={player.isListed ? onUnlist : onSell} disabled={sellButtonLocked} title={sellButtonLocked ? lockMessage : (player.isListed ? 'Unlist this player' : 'Sell this player to another club')}>{player.isListed ? 'Unlist Player' : 'Sell Player'}</button>
                        <button className="button danger" onClick={onRelease}>Release Player</button>
                    </>
                )}
                <button className="button secondary" onClick={() => { console.log('Compare button clicked!' + !!onCompare); soundService.playClick(); if (onCompare) onCompare(); }}>Compare Player</button>
                {!isPlayerTeamPlayer && onMakeOffer && (
                    <button className="button primary" onClick={onMakeOffer}>Make Offer</button>
                )}
            </div>
        </div>
    );
};
